import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cookie } from "lucide-react";
import { toast } from "sonner";
import { openCookieSettingsDialog } from "@/components/CookieBanner";
import OptimizedImage from "@/components/OptimizedImage";
import { useSignIn } from "@/hooks/useSignIn";

interface FooterProps {
  onSignIn: () => void;
}

const Footer = ({ onSignIn }: FooterProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signingIn } = useSignIn();
  
  // Wrap onSignIn to add loading state handling
  const handleSignInClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };

  // Scroll to top when route changes (except for hash links on same page)
  useEffect(() => {
    // Only scroll to top if there's no hash in the URL
    if (!location.hash) {
      // Use instant scroll immediately and multiple times to ensure it sticks
      window.scrollTo(0, 0);
      // Also use requestAnimationFrame to ensure it happens after render
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
      // Additional scrolls to catch any late updates
      setTimeout(() => window.scrollTo(0, 0), 10);
      setTimeout(() => window.scrollTo(0, 0), 50);
    }
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    const isMobile = window.innerWidth < 768;
    
    // Calculate header height dynamically
    const getHeaderHeight = () => {
      const header = document.querySelector('header');
      return header ? header.offsetHeight + 20 : 100; // Add 20px padding
    };

    // Calculate mobile offset accounting for header and banner
    const getMobileOffset = () => {
      const header = document.querySelector('header');
      // Find the special offer banner by looking for the element with bg-gradient-to-r text-white
      const banner = Array.from(document.querySelectorAll('div')).find(
        (el) => el.classList.contains('bg-gradient-to-r') && 
                el.classList.contains('text-white') &&
                el.classList.contains('static')
      );
      
      let offset = 0;
      
      // Add header height
      if (header) {
        offset += header.offsetHeight;
      }
      
      // Add banner height on mobile (banner is static on mobile)
      if (banner && isMobile) {
        offset += banner.offsetHeight;
      }
      
      // Add small padding for better visual spacing
      offset += 10;
      
      return offset;
    };

    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          if (isMobile) {
            // On mobile, use precise calculation to ensure exact positioning
            const mobileOffset = getMobileOffset();
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;
            
            requestAnimationFrame(() => {
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
              });
            });
          } else {
            const headerOffset = getHeaderHeight();
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        if (isMobile) {
          // On mobile, use precise calculation to ensure exact positioning
          const mobileOffset = getMobileOffset();
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;
          
          requestAnimationFrame(() => {
            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth'
            });
          });
        } else {
          const headerOffset = getHeaderHeight();
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  const handleLinkClick = (path: string, sectionId?: string) => {
    if (sectionId) {
      // For same-page sections, use smooth scroll
      scrollToSection(sectionId);
    } else {
      // For new pages, immediately scroll to top
      window.scrollTo(0, 0);
      // Navigate
      navigate(path);
      // Ensure scroll to top multiple times to catch any late renders or browser behavior
      setTimeout(() => window.scrollTo(0, 0), 10);
      setTimeout(() => window.scrollTo(0, 0), 50);
      setTimeout(() => window.scrollTo(0, 0), 100);
      setTimeout(() => window.scrollTo(0, 0), 200);
    }
  };

  const handleBlogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleLinkClick('/blog');
  };
  return (
    <footer className="w-full relative z-10 mt-12 sm:mt-20">
      {/* Main Footer Container */}
      <div className="bg-gradient-to-b from-background via-background to-muted/30 rounded-t-[2rem] sm:rounded-t-[3rem] border-t border-border/30 shadow-2xl relative overflow-hidden">
        {/* Background Watermark - Hidden on mobile */}
        <div className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none opacity-[0.02]">
          <div className="text-[20rem] font-black text-foreground/10 select-none" style={{ lineHeight: 1 }}>
            Seoro
          </div>
        </div>

        {/* Footer Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            {/* Left Section - Logo and Description */}
            <div className="lg:col-span-1 mb-6 lg:mb-0">
              <button
                onClick={() => navigate('/')}
                className="flex items-center mb-4 sm:mb-6 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="relative group">
                  <div className="h-10 w-10 sm:h-12 sm:w-12">
                    <OptimizedImage
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
                      alt="Seoro Logo"
                      width={48}
                      height={48}
                      priority={true}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
                <span className="ml-2 sm:ml-3 text-xl sm:text-2xl font-bold text-foreground">Seoro</span>
              </button>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-sm">
                Grow your organic traffic automatically. Create SEO content, build premium backlinks, and track real results—all in one place.
              </p>
            </div>

            {/* Navigation Columns */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-12">
                {/* Product Column */}
                <div className="mb-6 sm:mb-0">
                  <h3 className="font-bold text-foreground text-sm sm:text-base lg:text-lg mb-3 sm:mb-4 lg:mb-6">Product</h3>
                  <ul className="space-y-2.5 sm:space-y-3 lg:space-y-4">
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/', 'features')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Features
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/', 'pricing')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Pricing
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/integrations')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Integrations
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/updates')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Updates
                      </Button>
                    </li>
                  </ul>
                </div>

                {/* Resources Column */}
                <div className="mb-6 sm:mb-0">
                  <h3 className="font-bold text-foreground text-sm sm:text-base lg:text-lg mb-3 sm:mb-4 lg:mb-6">Resources</h3>
                  <ul className="space-y-2.5 sm:space-y-3 lg:space-y-4">
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/docs')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Documentation
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/guides')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Guides
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={handleBlogClick}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Blog
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/support')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Support
                      </Button>
                    </li>
                  </ul>
                </div>

                {/* Company Column */}
                <div className="col-span-2 sm:col-span-1 mb-0">
                  <h3 className="font-bold text-foreground text-sm sm:text-base lg:text-lg mb-3 sm:mb-4 lg:mb-6">Company</h3>
                  <ul className="space-y-2.5 sm:space-y-3 lg:space-y-4">
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/about')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        About
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/careers')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Careers
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/contact')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Contact
                      </Button>
                    </li>
                    <li>
                      <Button
                        variant="ghost"
                        onClick={() => handleLinkClick('/partners')}
                        className="p-0 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent font-normal text-sm sm:text-base transition-colors duration-200 justify-start"
                      >
                        Partners
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-4 sm:mt-12 lg:mt-16 pt-4 sm:pt-8 lg:pt-12 border-t border-border/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
              <div className="text-center sm:text-left w-full sm:w-auto">
                <h3 className="text-base sm:text-xl lg:text-2xl font-bold text-foreground mb-1 sm:mb-2">
                  Ready to boost your organic traffic?
                </h3>
                <p className="text-muted-foreground text-xs sm:text-base">
                  Join thousands of marketers growing their traffic with Seoro.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Button
                  onClick={handleSignInClick}
                  disabled={signingIn}
                  variant="outline"
                  className="w-full sm:w-auto rounded-full px-6 py-3 text-sm font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 flex items-center justify-center gap-3 backdrop-blur-sm bg-background/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <OptimizedImage
                    src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/google.82c899b0.svg"
                    alt="Google"
                    width={16}
                    height={16}
                    priority={true}
                    className="h-4 w-4"
                  />
                  Join with Google
                </Button>
                <Button
                  onClick={handleSignInClick}
                  disabled={signingIn}
                  className="w-full sm:w-auto rounded-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden group shadow-sm hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
                >
                  <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright and Legal */}
        <div className="border-t border-border/20 bg-muted/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="text-sm text-muted-foreground">
                © 2025 Seoro. All rights reserved.
              </div>
              <div className="flex items-center gap-6">
                <Button
                  variant="ghost"
                  onClick={() => handleLinkClick('/terms')}
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground hover:bg-transparent underline underline-offset-4 transition-colors duration-200"
                >
                  Terms of Service
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleLinkClick('/privacy')}
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground hover:bg-transparent underline underline-offset-4 transition-colors duration-200"
                >
                  Privacy Policy
                </Button>
                <Button
                  variant="ghost"
                  onClick={openCookieSettingsDialog}
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground hover:bg-transparent transition-colors duration-200 flex items-center gap-1.5"
                  title="Cookie Settings"
                >
                  <Cookie className="h-4 w-4" />
                  <span className="hidden sm:inline">Cookie Settings</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
